import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchLoveShots } from '../../store/slices/loveshotsSlice';
import { fetchSuggestions } from '../../store/slices/profileSlice';
import { fetchMatches } from '../../store/slices/matchesSlice';
import { sendLoveRequest, fetchLoveRequests } from '../../store/slices/loveRequestsSlice';
import { toast } from 'react-toastify';
import LoveShotCard from '../../components/features/loveshots/LoveShotCard';
import MatchCard from '../../components/features/matches/MatchCard';
import MatchesList from '../../components/features/matches/MatchesList';
import PageHeader from '../../components/common/PageHeader';
import { FaSearch, FaFilter, FaHeart, FaMapMarkerAlt, FaCalendarAlt, FaSpinner, FaUserPlus, FaMagic } from 'react-icons/fa';
import './Discover.css';

const Discover = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: loveshots, loading: loveshotsLoading } = useSelector((state) => state.loveshots);
  const { suggestions, loading: suggestionsLoading } = useSelector((state) => state.profile);
  const { sent: sentLoveRequests } = useSelector((state) => state.loveRequests);
  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('loveshots');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    gender: '',
    ageMin: '',
    ageMax: '',
    location: '',
    interests: []
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchLoveShots({ page: 1, discover: true }));
    dispatch(fetchSuggestions());
    dispatch(fetchLoveRequests());
  }, [dispatch]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFilters(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleViewProfile = (profile) => {
    const userId = profile.user?.id || profile.user_id || profile.id;
    navigate(`/profile/${userId}`);
  };

  const handleSendLove = async (profile) => {
    const receiverId = profile.user?.id || profile.user_id || profile.id;

    // Check if love request already sent
    const alreadySent = sentLoveRequests.some(req => req.receiver_id === receiverId && req.status === 'pending');
    if (alreadySent) {
      toast.error('Love request already sent');
      return;
    }

    try {
      const result = await dispatch(sendLoveRequest({
        receiverId: receiverId,
        message: `Salut ${profile.first_name}, j'aimerais faire connaissance !`,
        isSuperLike: false
      }));

      if (result.meta.requestStatus === 'fulfilled') {
        toast.success(`Love envoyé à ${profile.first_name} !`);
      } else {
        toast.error('Erreur lors de l\'envoi du Love');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du Love');
    }
  };

  const filteredSuggestions = suggestions.filter(profile => {
    // Exclure les profils Admin et Super Admin
    const userRole = profile.user?.role;
    if (userRole === 'admin' || userRole === 'superadmin') return false;


    if (searchTerm) {
      const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
      const username = profile.user?.username.toLowerCase();
      const search = searchTerm.toLowerCase();
      if (!fullName.includes(search) && !username.includes(search)) return false;
    }

    if (filters.gender && profile.gender !== filters.gender) return false;

    if (filters.location && !profile.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;

    if (filters.ageMin || filters.ageMax) {
      const age = new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear();
      if (filters.ageMin && age < parseInt(filters.ageMin)) return false;
      if (filters.ageMax && age > parseInt(filters.ageMax)) return false;
    }

    if (filters.interests.length > 0) {
      const profileInterests = Array.isArray(profile.interests) ? profile.interests : (profile.interests ? profile.interests.split(',').map(i => i.trim()) : []);
      if (!filters.interests.some(interest => profileInterests.includes(interest))) return false;
    }

    return true;
  });

  const commonInterests = [
    'Sport', 'Musique', 'Voyage', 'Cinéma', 'Lecture',
    'Cuisine', 'Art', 'Technologie', 'Nature', 'Animaux'
  ];

  return (
    <div className="discover-page">
      <div className="discover-container">
        {/* Header */}
        <PageHeader
          title="🔍 Découvrir"
          subtitle="Trouvez de nouvelles connexions et explorez le monde de CHATOO"
        />

        {/* Search and Filters */}
        <div className="discover-controls">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher des personnes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            Filtres
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Genre</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="M">Homme</option>
                  <option value="F">Femme</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Âge minimum</label>
                <input
                  type="number"
                  placeholder="18"
                  value={filters.ageMin}
                  onChange={(e) => handleFilterChange('ageMin', e.target.value)}
                  min="18"
                  max="100"
                />
              </div>

              <div className="filter-group">
                <label>Âge maximum</label>
                <input
                  type="number"
                  placeholder="50"
                  value={filters.ageMax}
                  onChange={(e) => handleFilterChange('ageMax', e.target.value)}
                  min="18"
                  max="100"
                />
              </div>

              <div className="filter-group">
                <label>Localisation</label>
                <input
                  type="text"
                  placeholder="Ville, région..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group full-width">
                <label>Centre d'intérêts</label>
                <div className="interests-grid">
                  {commonInterests.map(interest => (
                    <button
                      key={interest}
                      className={`interest-btn ${filters.interests.includes(interest) ? 'active' : ''}`}
                      onClick={() => handleInterestToggle(interest)}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="discover-tabs">
          <button
            className={`tab-btn ${activeTab === 'loveshots' ? 'active' : ''}`}
            onClick={() => setActiveTab('loveshots')}
          >
            <FaHeart /> LoveShots
          </button>
          <button
            className={`tab-btn ${activeTab === 'people' ? 'active' : ''}`}
            onClick={() => setActiveTab('people')}
          >
            <FaUserPlus /> Personnes
          </button>
        </div>

        {/* Content */}
        <div className="discover-content">
          {activeTab === 'loveshots' && (
            <div className="loveshots-section">
              <h2>LoveShots Populaires</h2>
              {loveshotsLoading ? (
                <div className="loading-container">
                  <FaSpinner className="spinner" />
                  <p>Chargement des LoveShots...</p>
                </div>
              ) : loveshots.length > 0 ? (
                <div className="loveshots-grid">
                  {loveshots.map((loveshot) => (
                    <LoveShotCard key={loveshot.id} loveshot={loveshot} />
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaHeart className="empty-icon" />
                  <h3>Aucun LoveShot trouvé</h3>
                  <p>Soyez le premier à partager un LoveShot !</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'people' && (
            <div className="people-section">
              <h2>Rencontrez de nouvelles personnes</h2>
              {suggestionsLoading ? (
                <div className="loading-container">
                  <FaSpinner className="spinner" />
                  <p>Recherche de profils...</p>
                </div>
              ) : filteredSuggestions.length > 0 ? (
                <div className="people-grid">
                  {filteredSuggestions.map((profile) => (
                    <div key={profile.id} className="person-card">
                      <div className="person-avatar">
                        <img
                          src={profile.photo_url || '/default-avatar.svg'}

                          alt={`${profile.first_name} ${profile.last_name}`}
                        />
                      </div>

                      <div className="person-info">
                        <h3>{profile.first_name} {profile.last_name}</h3>
                        <p className="username">@{profile.user?.username}</p>

                        <div className="person-details">
                          {profile.date_of_birth && (
                            <span className="detail">
                              <FaCalendarAlt />
                              {new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear()} ans
                            </span>
                          )}
                          {profile.location && (
                            <span className="detail">
                              <FaMapMarkerAlt />
                              {profile.location}
                            </span>
                          )}
                        </div>

                        {profile.bio && (
                          <p className="bio">{profile.bio.length > 100 ? `${profile.bio.substring(0, 100)}...` : profile.bio}</p>
                        )}

                        <div className="person-actions">
                          {sentLoveRequests.some(req => req.receiver_id === (profile.user?.id || profile.user_id || profile.id) && req.status === 'pending') ? (
                            <button
                              className="btn-love sent"
                              disabled
                            >
                              <FaHeart /> Love envoyé
                            </button>
                          ) : (
                            <button
                              className="btn-love"
                              onClick={() => handleSendLove(profile)}
                            >
                              <FaHeart /> Love
                            </button>
                          )}
                          <button
                            className="btn-profile"
                            onClick={() => handleViewProfile(profile)}
                          >
                            Voir profil
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaUserPlus className="empty-icon" />
                  <h3>Aucun profil trouvé</h3>
                  <p>Essayez de modifier vos filtres de recherche.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discover;
